import { Map } from 'immutable'
import { eventChannel } from 'redux-saga'
import {
  takeLatest,
  select,
  put,
  all,
  take,
  takeEvery,
  call,
  race,
} from 'redux-saga/effects'
import {
  ENTER_SIMULATE_MODE,
  EXIT_SIMULATE_MODE,
  PAUSE_SIMULATE_MODE,
  PLAY_SIMULATE_MODE,
  UPDATE_SIMULATION_SPEED,
  populateDefaultSimulationValues,
  updateSimulationEvaluations,
  updateSimulatedValue,
  ADVANCE_TICK,
  UPDATE_SIMULATED_VALUE,
  RESET_CYCLES,
  openSimulatedFuse,
  OPEN_SIMULATED_FUSE,
  UPDATE_SIMULATION_EVALUATIONS,
  resetSimulatedFuse,
  updateTimeRemainingVirtualFuseReset,
} from 'actions/feature/simulation'
import {
  getAllFunctionGraphs,
  orphanQuerySelector,
} from 'selectors/feature/functionGraphs'
import { conditionsSelector } from 'selectors/feature/conditions'
import simulationSelectors, {
  getPowerNetLevelSimulationValues,
  getSimulatedVirtualFuseRetries,
  getSimulationMeta,
} from 'selectors/feature/simulation'
import { signalsSelector } from 'selectors/feature/inputs'
import {
  getOutputsByOutputQuery,
  getOutputVirtualFuseSettings,
  getOutputs,
} from 'selectors/feature/outputs'
import { queriesSelector } from 'selectors/feature/queries'

import {
  deriveDefaultConditionSimulationValue,
  deriveSimulations,
  findAndResetTimeDependentQueries,
  createAllUrisArray,
  deriveOutputSimulatedEvaluations,
  handleDeriveRelatedInputValue,
} from 'utils/simulation'
import { isNull } from 'utils/types'
import {
  IGNITION_STATES,
  POWER_NET_LEVEL_SIGNAL_URIS,
  POWER_NET_LEVEL_SIMULATION_DEFAULTS,
} from 'constants/featureOutputs'
import { AUTOMATIC } from 'utils/constants'

function* handleDefaultSimulatedValues() {
  const [conditions, signals, queries] = yield all([
    select(conditionsSelector),
    select(signalsSelector),
    select(queriesSelector),
  ])

  const preparedDeriveDefaultConditionSimulationValue =
    deriveDefaultConditionSimulationValue(queries, signals)

  const defaultConditionSimulations = conditions.reduce(
    (acc, condition, key) => {
      const defaultValue =
        preparedDeriveDefaultConditionSimulationValue(condition)
      if (isNull(defaultValue)) {
        return acc
      }

      return acc.merge(defaultValue)
    },
    Map()
  )

  // signal map for power net level defaults
  const defaultPowerSignalSimulations = Object.entries(
    POWER_NET_LEVEL_SIGNAL_URIS
  ).reduce((defaults, [key, signalUri]) => {
    const value = POWER_NET_LEVEL_SIMULATION_DEFAULTS[key]
    const defaultValue = Map({
      [signalUri]: Map({ current: value, default: value }),
    })
    return defaults.merge(defaultValue)
  }, new Map())

  const defaultSimulations = defaultConditionSimulations.merge(
    defaultPowerSignalSimulations
  )

  yield put(populateDefaultSimulationValues(defaultSimulations))
}

const TIME_CONSTANT_MS = 100

function loop({ speed }) {
  return eventChannel((emitter) => {
    const iv = setInterval(() => {
      emitter(ADVANCE_TICK)
    }, TIME_CONSTANT_MS * speed)
    return () => {
      clearInterval(iv)
    }
  })
}

function* handleSimulationLoop({ speed }) {
  const chan = yield call(loop, { speed }) // create the interval as a channel
  try {
    while (true) {
      yield call(handleDeriveSimulationValues) // derive sim values
      yield take(chan) // blocks until interval fires, then restarts the `while` loop
    }
  } finally {
    chan.close() // close the channel's interval to prevent memory leaks.
  }
}

function* autoAdvanceSimulation() {
  while (true) {
    const { speed } = yield select(getSimulationMeta) // start by getting the current speed
    yield race([
      call(handleSimulationLoop, { speed }), // use the current speed to start the loop
      take(UPDATE_SIMULATION_SPEED), // break the current simulation loop and restart the `while` loop if speed is updated
    ])
  }
}
function* manualAdvanceSimulation() {
  // speed updates, simulated value changes, and reset cycles are handled elsewhere.
  // right now, the only thing we need to listen for in manual mode is when
  // the user clicks the 'advance' button
  yield takeEvery(ADVANCE_TICK, handleDeriveSimulationValues)
}

function* handleSimulationFlow() {
  while (true) {
    const { pause } = yield race({
      cycle: call(autoAdvanceSimulation), // default to auto cycling the simulation flow
      pause: take(PAUSE_SIMULATE_MODE), // break out of it if the user pauses auto simulation
    })
    if (pause) {
      yield race([
        call(manualAdvanceSimulation), // the user has paused, so start listening to 'manual' simulation stuff
        take(PLAY_SIMULATE_MODE), // cancel this race and restart the `while` loop if the user hits play again
      ])
    }
  }
}

export function* watchEnterSimulateMode() {
  // this is the entry point for the main simulation flow
  while (true) {
    yield take(ENTER_SIMULATE_MODE)
    yield call(handleDefaultSimulatedValues) // initial simulated value population
    yield race([
      take(EXIT_SIMULATE_MODE), // if the user exits, it cancels all of the downstream sagas
      call(handleSimulationFlow),
    ])
  }
}

function* handleDeriveSimulationValues() {
  const [
    conditions,
    currentSimulations,
    queries = Map(),
    functionGraphs,
    orphanQueries,
    outputsByQueryUri,
  ] = yield all([
    select(conditionsSelector),
    select(simulationSelectors.allConditionSimulationsSelector),
    select(queriesSelector),
    select(getAllFunctionGraphs),
    select(orphanQuerySelector),
    select(getOutputsByOutputQuery),
  ])
  const allUris = createAllUrisArray(
    orphanQueries,
    functionGraphs,
    outputsByQueryUri
  )

  const newSimulations = deriveSimulations(
    currentSimulations,
    queries.merge(conditions),
    allUris
  )

  const outputSimulations = deriveOutputSimulatedEvaluations(
    newSimulations,
    outputsByQueryUri,
    outputsByQueryUri
  )

  yield put(
    updateSimulationEvaluations(newSimulations.merge(outputSimulations))
  )
}

function* handleResetCycles() {
  const [conditions, queries] = yield all([
    select(conditionsSelector),
    select(queriesSelector),
  ])

  const resetSimulations = findAndResetTimeDependentQueries(conditions, queries)
  yield put(updateSimulationEvaluations(resetSimulations))
}

export function* resetTimeBasedValues() {
  yield takeLatest(RESET_CYCLES, handleResetCycles)
}

export function* handleUpdateSimulatedValue() {
  while (true) {
    const { uri, value } = yield take(UPDATE_SIMULATED_VALUE)
    const signals = yield select(signalsSelector)

    const relatedInputValues = handleDeriveRelatedInputValue(
      uri,
      value,
      signals
    )

    if (relatedInputValues) {
      yield all(
        relatedInputValues.map(([relatedUri, defaultValue]) =>
          put(updateSimulatedValue(relatedUri, defaultValue))
        )
      )
    }
  }
}

export function* watchFuses() {
  yield takeEvery(OPEN_SIMULATED_FUSE, handleOpenFuse)
  yield takeEvery(UPDATE_SIMULATION_EVALUATIONS, handleSimulatedOutputCurrent)
  yield takeEvery(UPDATE_SIMULATED_VALUE, handleIgnitionPowerCycle)
}

function* handleIgnitionPowerCycle({ uri, value }) {
  const ignitionCheck = (uri) =>
    uri === POWER_NET_LEVEL_SIGNAL_URIS.IGNITION_STATUS
  const { ignitionStatus } = yield select(getPowerNetLevelSimulationValues)
  // saga begins with "run" status
  // short-circuit if the uri is not related to ingition status signal
  // or the ignition is not in "run" or "accessory" state
  const isIgnitionOn = [
    IGNITION_STATES.RUN,
    IGNITION_STATES.ACCESSORY,
  ].includes(ignitionStatus)
  if (!ignitionCheck(uri) || !isIgnitionOn) return

  // saga proceeds with "off" status or returns
  const offCheck = yield take(UPDATE_SIMULATED_VALUE)
  if (ignitionCheck(offCheck.uri) && offCheck.value === IGNITION_STATES.OFF) {
    // saga proceeds from "off" to either "crank" then "run"
    // or from "off" to "run" straight from "off"
    const crankCheck = yield take(UPDATE_SIMULATED_VALUE)
    if (crankCheck.value === IGNITION_STATES.CRANK) {
      const runCheck = yield take(UPDATE_SIMULATED_VALUE)
      if (
        ignitionCheck(runCheck.uri) &&
        runCheck.value === IGNITION_STATES.RUN
      ) {
        yield put({ type: 'FUSE_IGNITION_RESET' })
      }
    } else if (
      ignitionCheck(crankCheck.uri) &&
      crankCheck.value === IGNITION_STATES.RUN
    ) {
      yield put({ type: 'FUSE_IGNITION_RESET' })
    }
  }
}

function* handleSimulatedOutputCurrent() {
  const outputs = yield select(getOutputs)
  const { ignitionStatus } = yield select(getPowerNetLevelSimulationValues)
  const simulations = yield select(
    simulationSelectors.allConditionSimulationsSelector
  )
  const isIgnitionOn = [
    IGNITION_STATES.RUN,
    IGNITION_STATES.ACCESSORY,
  ].includes(ignitionStatus)
  const fuses = Object.values(outputs)
    .filter(({ id: outputId, virtualFuseSetting }) => {
      const currentAmps = simulations.getIn([outputId, 'currentAmps'], 0)
      const isFuseOpen = simulations.getIn([outputId, 'fuseOpen'], false)
      if (!isIgnitionOn) {
        return false
      }
      if (
        virtualFuseSetting &&
        currentAmps >= virtualFuseSetting &&
        isFuseOpen === false
      ) {
        return true
      }
      return false
    })
    .map(({ id: outputId }) => {
      return put(openSimulatedFuse(outputId))
    })
  if (fuses.length > 0) {
    yield all(fuses)
  }
}

function* handleAutomaticFuseReset({ outputId }) {
  const { resetAttempts, resetDelay } = yield select(
    getOutputVirtualFuseSettings,
    { id: outputId }
  )
  const { speed } = yield select(getSimulationMeta)
  const retries = yield select(getSimulatedVirtualFuseRetries, { outputId })
  const countdownInterval = speed * TIME_CONSTANT_MS
  const framesUntilReset = (resetDelay * 100) / countdownInterval
  let frameIndex = 0

  function* handleSimulationFrame() {
    const frameCountdown = framesUntilReset - frameIndex
    const timeRemainingMs = countdownInterval * frameCountdown

    if (frameIndex === null) return

    frameIndex += 1
    if (frameIndex >= framesUntilReset) {
      yield put({ type: 'FUSE_TIMEOUT' })
      frameIndex = null
    }

    if (frameIndex % framesUntilReset === 0) {
      if (retries <= resetAttempts) {
        // reset the state of the fuse and incrememt the retry count
        if (retries !== resetAttempts)
          yield put(resetSimulatedFuse(outputId, retries + 1))
        frameIndex = null
      }
    }
    if (retries <= resetAttempts) {
      yield put(updateTimeRemainingVirtualFuseReset(outputId, timeRemainingMs))
    }
  }

  yield race([
    yield takeEvery(UPDATE_SIMULATION_EVALUATIONS, handleSimulationFrame),
    yield take('FUSE_TIMEOUT'),
  ])
}

function* handleIgnitionFuseReset({ outputId, outputProps }) {
  let frameIndex = 0

  // this is a noop for the ignition fuse reset
  function* handleSimulationFrame() {
    frameIndex = null
    if (frameIndex === null) yield null
  }

  yield race([
    yield takeEvery(UPDATE_SIMULATION_EVALUATIONS, handleSimulationFrame),
    yield take('FUSE_IGNITION_RESET'),
  ])
}

function* handleOpenFuse({ outputId, outputProps }) {
  const { resetType } = yield select(getOutputVirtualFuseSettings, {
    id: outputId,
  })

  if (resetType === AUTOMATIC) {
    yield handleAutomaticFuseReset({ outputId, outputProps })
  } else {
    yield handleIgnitionFuseReset({ outputId, outputProps })
  }
}
